import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import { statsAPI } from '../utils/api';

const modules = [
    { label: 'OPD Registration', icon: '🏥', path: '/opd/register', color: '#1e3a5f', gradient: 'linear-gradient(135deg, #1e3a5f, #2d5a8e)', desc: 'Outpatient visit registration', badge: 'OPD' },
    { label: 'IPD Admission', icon: '🛏️', path: '/ipd', color: '#065f46', gradient: 'linear-gradient(135deg, #065f46, #047857)', desc: 'Inpatient ward admission', badge: 'IPD' },
    { label: 'ER — Emergency', icon: '🚨', path: '/ae/new', color: '#dc2626', gradient: 'linear-gradient(135deg, #dc2626, #b91c1c)', desc: 'Emergency triage & registration', badge: 'EMERGENCY' },
    { label: 'Laboratory', icon: '🔬', path: '/lab/order', color: '#7c3aed', gradient: 'linear-gradient(135deg, #7c3aed, #6d28d9)', desc: 'Lab test orders & worklist', badge: 'LAB' },
    { label: 'Radiology / PACS', icon: 'PACS', path: '/pacs/register', color: '#0369a1', gradient: 'linear-gradient(135deg, #0369a1, #0284c7)', desc: 'X-Ray, CT, MRI, Ultrasound', badge: 'RADIOLOGY' },
    { label: 'Psychiatry', icon: '🧠', path: '/psych', color: '#7c3aed', gradient: 'linear-gradient(135deg, #7c3aed, #a78bfa)', desc: 'Mental health case management', badge: 'PSYCH' },
    { label: 'Blood Bank', icon: '🩸', path: '/blood-bank', color: '#be123c', gradient: 'linear-gradient(135deg, #be123c, #e11d48)', desc: 'Blood requests & inventory', badge: 'BLOOD BANK' },
    { label: 'Pharmacy', icon: '💊', path: '/pharmacy', color: '#0f766e', gradient: 'linear-gradient(135deg, #0f766e, #0d9488)', desc: 'Medicines & dispensing', badge: 'PHARMACY' },
    { label: 'Physiotherapy', icon: '🦽', path: '/physio', color: '#0369a1', gradient: 'linear-gradient(135deg, #0369a1, #0ea5e9)', desc: 'Rehab & physical therapy', badge: 'PHYSIO' },
    { label: 'ICU / CCU', icon: '🫀', path: '/icu', color: '#9f1239', gradient: 'linear-gradient(135deg, #9f1239, #be123c)', desc: 'Intensive & critical care unit', badge: 'ICU' },
];

export default function Dashboard() {
    const { user } = useAuthStore();
    const navigate = useNavigate();
    const hour = new Date().getHours();
    const greeting = hour < 12 ? 'Good Morning' : hour < 17 ? 'Good Afternoon' : 'Good Evening';

    const [liveStats, setLiveStats] = useState({ opd_today: '…', er_active: '…', ipd_admitted: '…', lab_pending: '…' });

    useEffect(() => {
        const load = () => statsAPI.get().then(r => setLiveStats(r.data)).catch(() => {});
        load();
        const t = setInterval(load, 30000);
        return () => clearInterval(t);
    }, []);

    const stats = [
        { label: 'OPD Today', value: liveStats.opd_today, icon: '🏥', color: '#1e3a5f', bg: '#eff6ff' },
        { label: 'ER Active', value: liveStats.er_active, icon: '🚨', color: '#dc2626', bg: '#fef2f2' },
        { label: 'IPD Admitted', value: liveStats.ipd_admitted, icon: '🛏️', color: '#065f46', bg: '#f0fdf4' },
        { label: 'Lab Pending', value: liveStats.lab_pending, icon: '🔬', color: '#7c3aed', bg: '#faf5ff' },
    ];

    return (
        <div>
            <div style={{ marginBottom: 28 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                        <h1 style={{ fontSize: 26, fontWeight: 800, color: '#1e293b', margin: 0 }}>
                            {greeting} 👋
                        </h1>
                        <p style={{ color: '#64748b', marginTop: 4, fontSize: 13 }}>Welcome to HIMS — Hospital Information Management System</p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#1e3a5f' }}>
                            {new Date().toLocaleDateString('en-PK', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                        </div>
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>🟢 System Online</div>
                    </div>
                </div>
            </div>

            {/* Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 28 }}>
                {stats.map(s => (
                    <div key={s.label} style={{ background: s.bg, borderRadius: 12, padding: '18px 20px', border: `1px solid ${s.color}22`, display: 'flex', alignItems: 'center', gap: 14 }}>
                        <div style={{ fontSize: 32 }}>{s.icon}</div>
                        <div>
                            <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
                            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>{s.label}</div>
                        </div>
                    </div>
                ))}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>Quick Access</span>
                <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
            </div>

            {/* Module Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 }}>
                {modules.map((mod) => (
                    <div key={mod.path} onClick={() => navigate(mod.path)}
                        style={{ background: 'white', borderRadius: 14, overflow: 'hidden', cursor: 'pointer', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', transition: 'transform 0.15s, box-shadow 0.15s' }}
                        onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 10px 28px rgba(0,0,0,0.12)'; }}
                        onMouseOut={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)'; }}
                    >
                        <div style={{ background: mod.gradient, padding: '18px 20px 14px', position: 'relative', overflow: 'hidden' }}>
                            <div style={{ position: 'absolute', top: -10, right: -10, width: 70, height: 70, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
                            <div style={{ fontSize: 34, marginBottom: 4 }}>
                                {mod.icon === 'PACS' ? (
                                    <svg width="46" height="42" viewBox="0 0 46 42" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        {/* C-arm arc - thick prominent C shape */}
                                        <path d="M 32 30 C 32 30 44 30 44 18 C 44 6 32 6 32 6" stroke="white" strokeWidth="5" fill="none" strokeLinecap="round"/>
                                        {/* X-ray source - top of C */}
                                        <rect x="26" y="2" width="10" height="6" rx="2" fill="white"/>
                                        <rect x="29" y="8" width="4" height="3" rx="1" fill="rgba(255,255,255,0.7)"/>
                                        {/* Detector panel - bottom of C */}
                                        <rect x="24" y="31" width="12" height="5" rx="2" fill="white"/>
                                        <rect x="26" y="28" width="8" height="3" rx="1" fill="rgba(255,255,255,0.6)"/>
                                        {/* Cart/base body */}
                                        <rect x="18" y="22" width="12" height="12" rx="2" fill="rgba(255,255,255,0.85)"/>
                                        {/* Cart wheels */}
                                        <circle cx="21" cy="37" r="2.5" stroke="white" strokeWidth="1.5" fill="rgba(255,255,255,0.25)"/>
                                        <circle cx="27" cy="37" r="2.5" stroke="white" strokeWidth="1.5" fill="rgba(255,255,255,0.25)"/>
                                        {/* Monitor arm */}
                                        <rect x="8" y="14" width="2" height="8" rx="1" fill="rgba(255,255,255,0.8)"/>
                                        <rect x="8" y="22" width="12" height="2" rx="1" fill="rgba(255,255,255,0.8)"/>
                                        {/* Monitor screen */}
                                        <rect x="2" y="6" width="10" height="8" rx="2" stroke="white" strokeWidth="1.5" fill="rgba(255,255,255,0.2)"/>
                                        <rect x="4" y="8" width="6" height="4" rx="0.5" fill="rgba(255,255,255,0.6)"/>
                                    </svg>
                                ) : mod.icon}
                            </div>
                            <span style={{ fontSize: 9, fontWeight: 700, background: 'rgba(255,255,255,0.2)', color: 'white', padding: '2px 8px', borderRadius: 10, letterSpacing: 1 }}>{mod.badge}</span>
                        </div>
                        <div style={{ padding: '14px 18px' }}>
                            <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b', marginBottom: 4 }}>{mod.label}</div>
                            <div style={{ fontSize: 11, color: '#64748b' }}>{mod.desc}</div>
                        </div>
                    </div>
                ))}
            </div>

            <div style={{ marginTop: 28, background: 'linear-gradient(135deg, #f0f9ff, #f0fdf4)', border: '1px solid #bae6fd', borderRadius: 12, padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
                <span style={{ fontSize: 24 }}>⚕️</span>
                <div>
                    <p style={{ fontSize: 13, color: '#0c4a6e', fontWeight: 700, margin: 0 }}>HIMS v2.0</p>
                    <p style={{ fontSize: 12, color: '#0369a1', margin: '2px 0 0' }}>OPD, IPD, ER, Lab, Radiology, Pharmacy, Blood Bank, ICU, Physiotherapy, Psychiatry & Queue modules live.</p>
                </div>
            </div>
        </div>
    );
}
